import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, Search, AlertCircle, LayoutDashboard } from "lucide-react";
import Header from "../../../common/layout/Header";
import AISuggestionForm from "../AISuggestionForm/AISuggestionForm";
import DomainSearchResults from "../DomainSearchResults/DomainSearchResults";
import { searchDomains, DomainSearchResult, getCustomerDetails } from "@/services/dreamscapeApi";
import { addDomainToCart } from "@/utils/cart";
import { AnimatedButton } from "@/components/common/animations";
import { getSession } from "@/utils/session";
import { CurrencyCode } from "@/utils/currencyConverter";

// Valid multi-part TLDs
const multiPartTlds = ["com.au", "net.au", "org.au", "co.uk"];

// ── Typewriter phrases — defined outside component so array identity is stable ──
const TYPEWRITER_WORDS = [
    "you always want",
    "that builds trust",
    "that gets noticed",
    "that stands out",
    "that grows with you",
];

// ── Typewriter hook ────────────────────────────────────────────────────────────
function useTypewriter(words: string[], typingSpeed = 80, deletingSpeed = 40, pauseMs = 1800) {
    const [displayed, setDisplayed] = useState("");
    const [wordIndex, setWordIndex] = useState(0);
    const [isDeleting, setIsDeleting] = useState(false);
    const [showCursor, setShowCursor] = useState(true);

    // Stable ref — avoids re-running the effect when the array reference changes
    const wordsRef = useRef(words);

    // Blinking cursor
    useEffect(() => {
        const id = setInterval(() => setShowCursor(v => !v), 530);
        return () => clearInterval(id);
    }, []);

    // Typing / deleting loop
    useEffect(() => {
        const current = wordsRef.current[wordIndex % wordsRef.current.length];

        if (!isDeleting && displayed === current) {
            const id = setTimeout(() => setIsDeleting(true), pauseMs);
            return () => clearTimeout(id);
        }

        if (isDeleting && displayed === "") {
            setIsDeleting(false);
            setWordIndex(i => i + 1);
            return;
        }

        const speed = isDeleting ? deletingSpeed : typingSpeed;
        const id = setTimeout(() => {
            setDisplayed(isDeleting
                ? current.slice(0, displayed.length - 1)
                : current.slice(0, displayed.length + 1)
            );
        }, speed);

        return () => clearTimeout(id);
        // wordsRef is stable so intentionally omitted from deps
    }, [displayed, isDeleting, wordIndex, typingSpeed, deletingSpeed, pauseMs]);

    return { displayed, showCursor };
}

export default function DomainHero() {
    const navigate = useNavigate();
    const [searchQuery, setSearchQuery] = useState("");
    const [focused, setFocused] = useState(false);
    const [isAISuggestionOpen, setIsAISuggestionOpen] = useState(false);

    const [searchResults, setSearchResults] = useState<DomainSearchResult[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [hasSearched, setHasSearched] = useState(false);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const resultsPerPage = 7;

    const [syncTrigger, setSyncTrigger] = useState(0);

    // Sync currency if logged in but preference is missing
    useEffect(() => {
        const session = getSession();
        const hasManualPref = localStorage.getItem('preferredCurrency');

        if (session && !hasManualPref) {
            getCustomerDetails(session.customerId).then(res => {
                if (res.success && res.customer) {
                    const country = (res.customer.country || res.customer.country_code || '').toUpperCase();
                    let prefCurrency: CurrencyCode = 'AUD';
                    if (country === 'NP' || country === 'NEPAL') prefCurrency = 'NRS';
                    else if (country === 'AU' || country === 'AUSTRALIA') prefCurrency = 'AUD';

                    localStorage.setItem('preferredCurrency', prefCurrency);
                    setSyncTrigger(prev => prev + 1); // Force re-render once synced
                }
            }).catch(err => console.error("DomainHero currency sync error:", err));
        }
    }, []);

    const { displayed, showCursor } = useTypewriter(TYPEWRITER_WORDS);

    // Suggestion pills (shown before search)
    const suggestions =
        searchQuery.length >= 2 && !hasSearched
            ? ["com", "io", "net", "co", "app", "dev"].map(
                (t) => `${searchQuery.toLowerCase()}.${t}`
            )
            : [];

    // Extract base domain and TLD from query
    const extractDomainParts = (query: string): { base: string; tld: string | null; fullDomain: string | null } => {
        const trimmed = query.trim().toLowerCase();
        let cleanQuery = trimmed.replace(/^https?:\/\//, "").replace(/^www\./, "").trim();
        cleanQuery = cleanQuery.replace(/\.+$/, "");

        if (!cleanQuery || cleanQuery.length === 0) {
            return { base: "", tld: null, fullDomain: null };
        }

        const parts = cleanQuery.split(".").filter((part) => part.length > 0);
        let base = cleanQuery;
        let tld: string | null = null;
        let fullDomain: string | null = null;

        if (parts.length >= 2) {
            const lastTwo = parts.slice(-2).join(".");
            if (multiPartTlds.includes(lastTwo)) {
                base = parts.slice(0, -2).join(".");
                tld = lastTwo;
                fullDomain = cleanQuery;
            } else if (parts.length === 2) {
                base = parts[0];
                tld = parts[1];
                fullDomain = cleanQuery;
            } else {
                base = parts[0];
                tld = parts.slice(1).join(".");
                fullDomain = cleanQuery;
            }
        }

        if (!base || base.length < 1) {
            return { base: cleanQuery, tld: null, fullDomain: null };
        }

        return { base, tld, fullDomain };
    };

    const isValidDomain = (domain: string): boolean => {
        if (!domain || domain.trim().length === 0) return false;
        const cleanDomain = domain.trim().replace(/^\.+|\.+$/g, "");
        if (cleanDomain.length === 0) return false;
        if (cleanDomain.includes("..") || cleanDomain.startsWith(".") || cleanDomain.endsWith(".")) return false;
        const domainRegex = /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?(\.[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?)*$/i;
        if (!domainRegex.test(cleanDomain)) return false;
        const parts = cleanDomain.split(".");
        if (parts.length < 2) return false;
        if (parts.some((part) => part.length === 0)) return false;
        return true;
    };

    const performSearch = useCallback(async (query: string) => {
        if (!query.trim() || query.trim().length < 2) {
            setSearchResults([]);
            setHasSearched(false);
            return;
        }

        setLoading(true);
        setError(null);
        setHasSearched(true);

        try {
            const { base, fullDomain } = extractDomainParts(query);
            const domainsToSearch: string[] = [];

            if (fullDomain && isValidDomain(fullDomain)) {
                domainsToSearch.push(fullDomain.trim().replace(/^\.+|\.+$/g, ""));
            }

            const commonTlds = [
                "com", "net", "org", "io", "co", "info", "biz", "xyz", "app", "dev",
                "tech", "online", "au", "com.au", "net.au", "org.au", "co.uk", "sydney", "melbourne",
            ];

            commonTlds.forEach((commonTld) => {
                if (fullDomain && (fullDomain === `${base}.${commonTld}` || fullDomain.endsWith(`.${commonTld}`))) return;

                const domain = `${base}.${commonTld}`;
                if (!isValidDomain(domain)) return;

                const cleanDomain = domain.trim().replace(/^\.+|\.+$/g, "");
                const domainParts = cleanDomain.split(".");

                if (domainParts.length >= 2) {
                    const lastPart = domainParts[domainParts.length - 1];
                    const secondLastPart = domainParts[domainParts.length - 2];
                    if (lastPart === secondLastPart) return;

                    if (multiPartTlds.includes(commonTld)) {
                        const tldParts = commonTld.split(".");
                        if (base === tldParts[0]) return;
                    }

                    const baseParts = base.split(".");
                    if (baseParts.length > 0) {
                        const baseLastPart = baseParts[baseParts.length - 1];
                        if (baseLastPart === commonTld || (multiPartTlds.includes(commonTld) && commonTld.startsWith(`${baseLastPart}.`))) return;
                    }
                }

                if (!domainsToSearch.includes(cleanDomain)) {
                    domainsToSearch.push(cleanDomain);
                }
            });

            const response = await searchDomains(base, [], domainsToSearch);

            if (response.success) {
                const topTlds = [
                    "com", "net", "org", "io", "co", "info", "biz", "xyz", "app", "dev",
                    "tech", "online", "au", "com.au", "net.au", "org.au", "co.uk", "sydney", "melbourne",
                ];

                const getTld = (domain: string): string => {
                    const parts = domain.split(".");
                    if (parts.length >= 2) {
                        const lastTwo = parts.slice(-2).join(".");
                        if (multiPartTlds.includes(lastTwo)) return lastTwo;
                        return parts[parts.length - 1];
                    }
                    return "";
                };

                const getTldPriority = (domain: string): number => {
                    const index = topTlds.indexOf(getTld(domain));
                    return index === -1 ? 999 : index;
                };

                const queryLower = query.trim().toLowerCase();
                const sortedResults = [...response.results].sort((a, b) => {
                    const aIsExact = a.domain.toLowerCase() === queryLower;
                    const bIsExact = b.domain.toLowerCase() === queryLower;
                    if (aIsExact && !bIsExact) return -1;
                    if (!aIsExact && bIsExact) return 1;

                    const aPrio = getTldPriority(a.domain);
                    const bPrio = getTldPriority(b.domain);
                    if (aPrio !== bPrio) return aPrio - bPrio;

                    if (a.available && !b.available) return -1;
                    if (!a.available && b.available) return 1;

                    return a.domain.localeCompare(b.domain);
                });

                setSearchResults(sortedResults);
                setCurrentPage(1);
                if (sortedResults.length === 0) {
                    setError("No domains found. Please try a different search term.");
                }
            } else {
                setError(response.message || "Failed to search domains. Please try again.");
                setSearchResults([]);
            }
        } catch {
            setError("An error occurred while searching. Please check your API configuration.");
            setSearchResults([]);
        } finally {
            setLoading(false);
        }
    }, []);

    // Debounced real-time search
    useEffect(() => {
        const timer = setTimeout(() => {
            if (searchQuery.trim().length >= 2) {
                performSearch(searchQuery);
            } else {
                setSearchResults([]);
                setHasSearched(false);
                setError(null);
            }
        }, 500);
        return () => clearTimeout(timer);
    }, [searchQuery, performSearch]);

    const handleSearch = () => {
        if (searchQuery.trim()) {
            if (import.meta.env.VITE_USE_DB === 'false') {
                const storefrontUrl = import.meta.env.VITE_DREAMSCAPE_STOREFRONT_URL || 'https://sandomains.com';
                const searchUrl = `${storefrontUrl.replace(/\/$/, '')}/domain-names/search/?domain=${encodeURIComponent(searchQuery.trim())}`;
                window.open(searchUrl, '_blank');
            } else {
                navigate(`/domains/search?q=${encodeURIComponent(searchQuery.trim())}`);
            }
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") handleSearch();
    };

    const handleRegister = (domain: string) => {
        if (import.meta.env.VITE_USE_DB === 'false') {
            const storefrontUrl = import.meta.env.VITE_DREAMSCAPE_STOREFRONT_URL || 'https://sandomains.com';
            const searchUrl = `${storefrontUrl.replace(/\/$/, '')}/domain-names/search/?domain=${encodeURIComponent(domain)}`;
            window.open(searchUrl, '_blank');
            return;
        }

        const domainResult = searchResults.find((r) => r.domain === domain);
        if (domainResult) {
            addDomainToCart({
                name: domainResult.domain,
                price: domainResult.registerPrice || 0,
                currency: domainResult.currency || "USD",
                renewPrice: domainResult.renewPrice,
                available: domainResult.available,
                period: 1,
                privacy: true,
            });
            // The CartSidebar component handles showing the cart and checkout redirection now
        }
    };

    return (
        <>
            <section className="bg-[#06060e]" id="domain-search">
                <Header />
                <div className="relative w-full flex flex-col items-center justify-center overflow-hidden px-4 sm:px-6 py-14 md:py-28">

                    {/* Ambient glow blobs */}
                    <div className="pointer-events-none absolute inset-0">
                        <div className="absolute top-[10%] left-[5%] w-[280px] h-[220px] sm:w-[500px] sm:h-[400px] rounded-full bg-indigo-600/10 blur-[80px] sm:blur-[120px]" />
                        <div className="absolute bottom-[15%] right-[5%] w-[220px] h-[180px] sm:w-[420px] sm:h-[340px] rounded-full bg-sky-500/10 blur-[80px] sm:blur-[120px]" />
                        <div className="absolute top-0 left-[40%] w-[160px] h-[120px] sm:w-[300px] sm:h-[200px] rounded-full bg-pink-500/5 blur-[60px] sm:blur-[100px]" />
                    </div>

                    {/* Subtle grid overlay */}
                    <div className="pointer-events-none absolute inset-0 opacity-[0.03] bg-[linear-gradient(rgba(99,102,241,1)_1px,transparent_1px),linear-gradient(90deg,rgba(99,102,241,1)_1px,transparent_1px)] bg-[size:60px_60px]" />

                    {/* Content */}
                    <div className="relative z-10 w-full max-w-4xl text-center">

                        {/* Heading */}
                        <h1 className="font-bold tracking-[-0.03em] leading-[1.12] text-white mb-4 sm:mb-5 text-[clamp(36px,7vw,84px)]">
                            Get the domain
                            <br />
                            {/* Fixed-height wrapper prevents layout shift as text length changes */}
                            <span className="inline-block min-h-[1.15em] align-bottom">
                                <span className="bg-gradient-to-r from-indigo-400 via-sky-400 to-emerald-400 bg-clip-text text-transparent">
                                    {displayed}
                                </span>
                                {/* Blinking cursor */}
                                <span
                                    className={`inline-block w-[3px] ml-1 rounded-full bg-indigo-400 transition-opacity duration-100 ${showCursor ? "opacity-100" : "opacity-0"
                                        }`}
                                    style={{ height: "0.85em", verticalAlign: "middle" }}
                                />
                            </span>
                        </h1>

                        {/* Subtext */}
                        <p className="text-white/55 font-light text-base sm:text-lg mb-5 sm:mb-6 leading-relaxed px-2">
                            Search, compare and register your perfect domain — in seconds.
                        </p>

                        {/* Dashboard link */}
                        <a
                            href="https://sandomains.com/login/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full border border-indigo-500/30 bg-indigo-500/10 hover:bg-indigo-500/20 hover:border-indigo-500/50 hover:shadow-[0_0_25px_rgba(99,102,241,0.25)] transition-all duration-500 text-sm font-semibold text-indigo-300 hover:text-white mb-6 sm:mb-8 group"
                        >
                            <LayoutDashboard className="w-4 h-4 text-indigo-400 group-hover:text-white transition-colors duration-300" />
                            Login to Your Dashboard
                        </a>

                        {/* ── Search bar ── */}
                        <div className={`flex flex-col sm:flex-row sm:items-center rounded-2xl px-5 sm:px-6 py-4 sm:py-3 gap-3 sm:gap-0 transition-all duration-300 ${focused
                            ? "bg-white/[0.14] border border-white/[0.40] shadow-[0_0_32px_rgba(99,102,241,0.25)]"
                            : "bg-white/[0.10] border border-white/[0.28] shadow-[0_0_20px_rgba(99,102,241,0.12)]"
                            }`}>
                            {/* Input row */}
                            <div className="flex items-center flex-1 min-w-0">
                                <Search className={`shrink-0 w-6 h-6 mr-3 transition-colors duration-300 ${focused ? "text-white/70" : "text-white/55"
                                    }`} />
                                <input
                                    type="text"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    onFocus={() => setFocused(true)}
                                    onBlur={() => setFocused(false)}
                                    onKeyDown={handleKeyDown}
                                    placeholder="Type the domain name you need"
                                    className="flex-1 min-w-0 bg-transparent border-none outline-none text-white placeholder-white/45 text-[17px] sm:text-[19px] py-1.5 sm:py-3 font-medium tracking-wide"
                                />
                            </div>

                            {/* AI Suggestions button */}
                            <div className="flex items-stretch sm:items-center sm:shrink-0 ml-3">
                                <AnimatedButton
                                    onClick={() => setIsAISuggestionOpen(true)}
                                    variant="secondary"
                                    className="w-full sm:w-auto"
                                >
                                    <Sparkles className="w-4 h-4 group-hover:scale-110 group-hover:rotate-12 transition-transform duration-300" />
                                    AI Suggestions
                                </AnimatedButton>
                            </div>
                        </div>

                        {/* ── TLD pills — non-clickable display text ── */}
                        <div className="flex flex-wrap items-center justify-center gap-3 mt-8">
                            <span className="text-white/40 font-medium mr-1 hidden sm:inline">Popular:</span>
                            {[
                                { tld: ".com", className: "text-indigo-300" },
                                { tld: ".co", className: "text-sky-300" },
                                { tld: ".net", className: "text-violet-300" },
                                { tld: ".org", className: "text-emerald-300" },
                                { tld: ".shop", className: "text-pink-300" },
                                { tld: ".ai", className: "text-amber-300" },
                            ].map(({ tld, className }) => (
                                <span
                                    key={tld}
                                    className={`px-4 py-1.5 rounded-full font-semibold border border-white/[0.10] bg-white/[0.04] select-none opacity-70 ${className}`}
                                >
                                    {tld}
                                </span>
                            ))}
                        </div>

                        {/* Suggestion pills (before search — query-based) */}
                        {suggestions.length > 0 && (
                            <div className="flex flex-wrap gap-2 justify-center mt-4">
                                {suggestions.map((s) => (
                                    <button
                                        key={s}
                                        onClick={() => setSearchQuery(s)}
                                        className="px-4 py-1.5 rounded-full text-sm bg-white/[0.05] hover:bg-indigo-500/15 border border-white/[0.10] hover:border-indigo-500/40 text-white/55 hover:text-indigo-300 transition-all duration-150 hover:-translate-y-px"
                                    >
                                        <span className="text-white/85 font-medium">{s}</span>
                                    </button>
                                ))}
                            </div>
                        )}

                        {/* Error message */}
                        {error && (
                            <div className="mt-5 flex items-center gap-2 text-red-400 bg-red-500/10 border border-red-500/30 rounded-xl p-3 text-left">
                                <AlertCircle className="w-5 h-5 shrink-0" />
                                <span className="text-sm">{error}</span>
                            </div>
                        )}

                        {/* Search Results */}
                        {(hasSearched || searchResults.length > 0) && (
                            <div className="mt-6 text-left">
                                <DomainSearchResults
                                    key={syncTrigger}
                                    results={searchResults}
                                    loading={loading}
                                    onRegister={handleRegister}
                                    currentPage={currentPage}
                                    resultsPerPage={resultsPerPage}
                                    onPageChange={setCurrentPage}
                                    onLoadMore={handleSearch}
                                />
                            </div>
                        )}
                    </div>
                </div>

                {/* AI Suggestion Form Popup */}
                <AISuggestionForm
                    isOpen={isAISuggestionOpen}
                    onClose={() => setIsAISuggestionOpen(false)}
                />
            </section>
        </>
    );
}